/**
 * Reconciliation Dashboard
 * Feature: 029-reconciliation-services (T056)
 * Constitution Version: 1.11.0
 *
 * Displays status cards, run history, and drift logs for all reconciliation services.
 * Conformed to Spec 197 design blueprint
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  GitCompareArrows,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import ReconciliationRunTable from '../../components/reconciliation/ReconciliationRunTable';
import ReconciliationLogTable from '../../components/reconciliation/ReconciliationLogTable';
import { reconciliationApi } from '../../services/reconciliationApi';
import {
  PageLayout,
  Card,
  StatCard,
  Badge,
  ErrorState,
  LoadingState,
  AppTooltip,
  PageHelpPanel,
} from '../../components/ui';
import { deployOpsHelp } from '../../data/help/deployOpsHelp';
import type { ReconciliationStatusCard } from '../../types/reconciliation.types';
import { formatEastern } from '@/utils/dateFormatter';

const SERVICE_NAMES: Record<string, string> = {
  AgentDeployment: 'Agent Deployment',
  WorkspaceMetadata: 'Workspace Metadata',
  ServicePrincipal: 'Service Principal',
};

type RunStatus = 'completed' | 'failed' | 'in_progress' | 'never_run';

const STATUS_CONFIG: Record<
  RunStatus,
  { icon: typeof CheckCircle2; variant: 'success' | 'error' | 'info' | 'warning'; label: string }
> = {
  completed: { icon: CheckCircle2, variant: 'success', label: 'Completed' },
  failed: { icon: XCircle, variant: 'error', label: 'Failed' },
  in_progress: { icon: Loader2, variant: 'info', label: 'Running' },
  never_run: { icon: AlertTriangle, variant: 'warning', label: 'Never Run' },
};

function deriveRunStatus(card: ReconciliationStatusCard): RunStatus {
  if (card.last_run_status) return card.last_run_status;
  if (card.status_indicator) {
    switch (card.status_indicator) {
      case 'green':
        return 'completed';
      case 'yellow':
        return 'in_progress';
      case 'red':
        return 'failed';
    }
  }
  return 'never_run';
}

/** Token-styled status tile for a single reconciliation service. */
const ServiceStatusCard: React.FC<{ card: ReconciliationStatusCard }> = ({ card }) => {
  const runStatus = deriveRunStatus(card);
  const statusConfig = STATUS_CONFIG[runStatus];
  const StatusIcon = statusConfig.icon;

  const title = card.service_display_name || SERVICE_NAMES[card.service_name] || card.service_name;
  const lastRunTime = card.last_run_time ?? card.last_run_timestamp ?? null;
  const driftsDetected = card.drifts_detected ?? card.drifts_detected_last_run ?? 0;
  const autoFixes = card.auto_fixes_applied ?? card.auto_fixes_applied_last_run ?? 0;
  const failures = card.failures ?? 0;
  const successRate = card.success_rate ?? card.success_rate_30_days ?? 0;
  const barVariant =
    successRate >= 95 ? 'success' : successRate >= 80 ? 'warning' : 'danger';

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{title}</h3>
        <Badge variant={statusConfig.variant}>
          <span className="flex items-center gap-1">
            <StatusIcon
              className={clsx('h-3.5 w-3.5', runStatus === 'in_progress' && 'animate-spin')}
            />
            {statusConfig.label}
          </span>
        </Badge>
      </div>

      {lastRunTime && (
        <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
          Last run: {formatEastern(lastRunTime)}
        </p>
      )}

      <dl className="space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-[var(--color-text-secondary)]">Drifts Detected</dt>
          <dd className="font-semibold text-[var(--color-text-primary)]">{driftsDetected}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-[var(--color-text-secondary)]">Auto-fixes Applied</dt>
          <dd className="font-semibold text-[var(--color-status-success)]">{autoFixes}</dd>
        </div>
        {failures > 0 && (
          <div className="flex items-center justify-between">
            <dt className="text-[var(--color-text-secondary)]">Failures</dt>
            <dd className="font-semibold text-[var(--color-status-danger)]">{failures}</dd>
          </div>
        )}
      </dl>

      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-[var(--color-text-secondary)]">Success Rate (30 days)</span>
          <span className="font-semibold text-[var(--color-text-primary)]">
            {successRate.toFixed(1)}%
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
          <div
            className={clsx(
              'h-full rounded-full',
              barVariant === 'success' && 'bg-[var(--color-status-success)]',
              barVariant === 'warning' && 'bg-[var(--color-status-warning)]',
              barVariant === 'danger' && 'bg-[var(--color-status-danger)]',
            )}
            style={{ width: `${Math.min(100, Math.max(0, successRate))}%` }}
          />
        </div>
      </div>
    </Card>
  );
};

const TABS = [
  { label: 'Run History', tip: 'View history of reconciliation runs with status and duration' },
  { label: 'Drift Logs', tip: 'View detected drift events and remediation outcomes' },
] as const;

export default function ReconciliationDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const queryClient = useQueryClient();

  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
    refetch,
  } = useQuery({
    queryKey: ['reconciliation', 'status'],
    queryFn: () => reconciliationApi.getStatus(),
    refetchInterval: 60000, // Refresh every minute
  });

  return (
    <PageLayout
      title="Reconciliation Services"
      description="Monitor drift detection and auto-fix operations for agents, workspaces, and service principals"
      icon={<GitCompareArrows className="h-6 w-6" />}
      actions={<PageHelpPanel content={deployOpsHelp} buttonSize="sm" />}
      // Spec 285 / 226: status cards + the run and log tables all share the 'reconciliation' prefix.
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ['reconciliation'] })}
    >
      {statusLoading ? (
        <Card className="p-4">
          <LoadingState rows={6} />
        </Card>
      ) : statusError ? (
        <ErrorState
          title="Failed to load reconciliation status"
          message={(statusError as Error).message}
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {/* Status Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {statusData?.status_cards?.map((card, index) =>
              index === 0 ? (
                <AppTooltip
                  key={card.service_name}
                  title="Reconciliation service status — shows drift detection and auto-fix state"
                >
                  <div>
                    <ServiceStatusCard card={card} />
                  </div>
                </AppTooltip>
              ) : (
                <ServiceStatusCard key={card.service_name} card={card} />
              ),
            )}
          </div>

          {/* Scheduler Status */}
          {statusData?.scheduler_status && (
            <Card className="mb-6 p-4">
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
                  Scheduler Status
                </h2>
                <Badge variant={statusData.scheduler_status.isShuttingDown ? 'warning' : 'success'}>
                  {statusData.scheduler_status.isShuttingDown ? 'Shutting down' : 'Running'}
                </Badge>
              </div>
              <div className="space-y-1">
                {statusData.scheduler_status.tasks?.map((task) => (
                  <p key={task.name} className="text-sm text-[var(--color-text-secondary)]">
                    <span className="font-medium text-[var(--color-text-primary)]">{task.name}</span>
                    {': '}
                    {task.isRunning ? 'Running' : 'Idle'} &bull; Schedule: {task.schedule}
                    {task.lastRun && ` • Last run: ${formatEastern(task.lastRun)}`}
                  </p>
                ))}
              </div>
            </Card>
          )}

          {/* Tabs for Run History and Logs */}
          <Card padding="none">
            <div className="flex gap-1 border-b border-[var(--color-border)] px-2">
              {TABS.map((tab, i) => (
                <AppTooltip key={tab.label} title={tab.tip}>
                  <button
                    type="button"
                    onClick={() => setTabValue(i)}
                    aria-selected={tabValue === i}
                    className={clsx(
                      '-mb-px border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                      tabValue === i
                        ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                        : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                    )}
                  >
                    {tab.label}
                  </button>
                </AppTooltip>
              ))}
            </div>
            <div className="p-4">
              {tabValue === 0 ? <ReconciliationRunTable /> : <ReconciliationLogTable />}
            </div>
          </Card>
        </>
      )}
    </PageLayout>
  );
}
